// 타자기(타이핑) 효과 — 글자가 하나씩 나타나고 깜빡이는 커서를 표시한다.
import type {
  TextEffectConfig,
  TextEffectDef,
  TextEffectRender,
} from './types';

/**
 * 현재 프레임에서 보여줄 글자 수.
 * - frame ≤ 0 → 0
 * - durationInSeconds ≤ 0 → 즉시 전체(타이핑 효과 없음)
 * - 그 외 → floor(진행비율 × 글자수), 0..textLength 로 클램프
 */
export function typedCharCount(
  textLength: number,
  frame: number,
  fps: number,
  durationInSeconds: number,
): number {
  if (textLength <= 0) return 0;
  if (durationInSeconds <= 0) return textLength;
  if (frame <= 0) return 0;
  const totalFrames = durationInSeconds * fps;
  const ratio = frame / totalFrames;
  const count = Math.floor(ratio * textLength);
  return Math.max(0, Math.min(textLength, count));
}

/**
 * 깜빡이는 커서의 현재 on/off.
 * 1초에 blinkPerSecond 번 깜빡(한 주기 = fps/blinkPerSecond 프레임), 전반부 on / 후반부 off.
 */
export function isCursorOn(
  frame: number,
  fps: number,
  blinkPerSecond = 2,
): boolean {
  const cycleFrames = fps / blinkPerSecond;
  if (cycleFrames <= 0) return true;
  const phase = ((frame % cycleFrames) + cycleFrames) % cycleFrames;
  return phase < cycleFrames / 2;
}

/** 타자기 효과 정의(레지스트리 등록 대상). */
export const typewriterEffect: TextEffectDef = {
  type: 'typewriter',
  // 인스펙터에서 t(label) 로 해석되는 i18n 키.
  label: 'Editor.inspector.text.effect-typewriter',
  defaultConfig: {
    type: 'typewriter',
    durationInSeconds: 1.5,
    showCursor: true,
  },
  compute: (
    fullText: string,
    frame: number,
    fps: number,
    config: TextEffectConfig,
  ): TextEffectRender => {
    // 방어적 판별 — 레지스트리 디스패치상 항상 typewriter 지만 타입 안전 위해 확인.
    if (config.type !== 'typewriter') {
      return { visibleText: fullText, cursor: false };
    }
    const n = typedCharCount(
      fullText.length,
      frame,
      fps,
      config.durationInSeconds,
    );
    return {
      visibleText: fullText.slice(0, n),
      cursor: config.showCursor && isCursorOn(frame, fps),
    };
  },
};
